import { useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Phone, Plus, Trash2, ChevronDown, ChevronUp, Star } from 'lucide-react';
import { cn } from '../lib/utils';
import { formatPhoneInput, isValidPhone } from '../lib/phone';

const PHONE_TYPES = [
  { value: 'work', label: 'Work' },
  { value: 'home', label: 'Home' },
  { value: 'cell', label: 'Cell' },
  { value: 'fax', label: 'Fax' },
  { value: 'other', label: 'Other' },
];

const TYPE_COLORS = {
  work: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  home: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  cell: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  fax: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  other: 'bg-muted text-muted-foreground',
};

const newPhone = (overrides = {}) => ({
  phone_id: `tmp_${Math.random().toString(36).slice(2, 10)}`,
  type: 'work',
  number: '',
  is_primary: false,
  ...overrides,
});

/**
 * Expandable multi-phone editor.
 * Props:
 *  - value: array of { phone_id, type, number, is_primary }
 *  - onChange: (newArray) => void
 */
export function MultiPhoneEditor({ value = [], onChange }) {
  const phones = value.length > 0 ? value : [newPhone({ is_primary: true })];
  const [expandedAll, setExpandedAll] = useState(false);

  const update = (idx, patch) => {
    const next = phones.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    onChange(next);
  };

  const remove = (idx) => {
    let next = phones.filter((_, i) => i !== idx);
    // Ensure at least one primary
    if (next.length > 0 && !next.some(p => p.is_primary)) {
      next = next.map((p, i) => i === 0 ? { ...p, is_primary: true } : p);
    }
    onChange(next);
  };

  const add = () => {
    onChange([...phones, newPhone()]);
    setExpandedAll(true);
  };

  const setPrimary = (idx) => {
    onChange(phones.map((p, i) => ({ ...p, is_primary: i === idx })));
  };

  // Default behaviour: show primary fully; collapse extras into chips when more than 1
  const extras = phones.length - 1;
  const showAll = expandedAll || phones.length <= 1;

  return (
    <div className="space-y-3" data-testid="multi-phone-editor">
      {phones.map((p, idx) => {
        const isPrimary = p.is_primary;
        const collapsed = !showAll && !isPrimary;
        if (collapsed) return null;
        return (
          <div
            key={p.phone_id}
            className={cn(
              'rounded-xl border p-3 space-y-2 transition-colors',
              isPrimary ? 'border-primary/40 bg-primary/5' : 'border-border bg-card'
            )}
            data-testid={`phone-row-${idx}`}
          >
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={p.type} onValueChange={(v) => update(idx, { type: v })}>
                <SelectTrigger className="w-full sm:w-32 h-10" data-testid={`phone-type-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PHONE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="relative flex-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={p.number}
                  onChange={(e) => update(idx, { number: formatPhoneInput(e.target.value) })}
                  placeholder="(555) 123-4567"
                  className={cn(
                    'pl-10 h-10',
                    p.number && !isValidPhone(p.number) && 'border-amber-500 focus:border-amber-500',
                  )}
                  maxLength={14}
                  data-testid={`phone-number-${idx}`}
                />
                {p.number && !isValidPhone(p.number) && (
                  <span className="absolute -bottom-4 left-0 text-[10px] text-amber-600">
                    Enter all 10 digits
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="icon"
                  variant={isPrimary ? 'default' : 'ghost'}
                  className="h-10 w-10 flex-shrink-0"
                  onClick={() => setPrimary(idx)}
                  title={isPrimary ? 'Primary phone' : 'Make primary'}
                  data-testid={`phone-primary-${idx}`}
                >
                  <Star className={cn('w-4 h-4', isPrimary && 'fill-current')} />
                </Button>
                {phones.length > 1 && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-10 w-10 text-destructive flex-shrink-0"
                    onClick={() => remove(idx)}
                    data-testid={`phone-remove-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className={cn('text-[10px] capitalize', TYPE_COLORS[p.type])}>
                {PHONE_TYPES.find(t => t.value === p.type)?.label || p.type}
              </Badge>
              {isPrimary && <Badge className="text-[10px] bg-primary text-primary-foreground">Primary</Badge>}
            </div>
          </div>
        );
      })}

      {!showAll && extras > 0 && (
        <button
          type="button"
          onClick={() => setExpandedAll(true)}
          className="text-xs text-primary hover:underline flex items-center gap-1"
          data-testid="phones-expand-btn"
        >
          <ChevronDown className="w-3 h-3" /> Show {extras} more phone{extras > 1 ? 's' : ''}
        </button>
      )}
      {showAll && phones.length > 1 && (
        <button
          type="button"
          onClick={() => setExpandedAll(false)}
          className="text-xs text-muted-foreground hover:underline flex items-center gap-1"
        >
          <ChevronUp className="w-3 h-3" /> Collapse
        </button>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        className="w-full sm:w-auto"
        data-testid="add-phone-btn"
      >
        <Plus className="w-3.5 h-3.5 mr-1" /> Add phone number
      </Button>
    </div>
  );
}

export default MultiPhoneEditor;
